/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
import getOr from 'lodash/fp/getOr.js';
import { createSelector } from 'reselect';
import { currentModelId, modelsById } from '#selectors/entities/models.js';
import sectionNameFromFullyQualifiedFieldName from '#support/models/modelPage/sectionNameFromFullyQualifiedFieldName.js';

// Mirrors selectInvisibleFields' safe (non-throwing) read of the redux-form slice's own
// visibleFields map (#selectors/ui/form/model.js), rather than makeSelectVisibleFields,
// which assumes state.form[form] already exists.
const selectVisibleFieldsMap = form => state => getOr({}, `form[${form}].visibleFields`, state);

// Safe (non-throwing) read of the redux-form slice's aiStatus/provenance maps.
const selectAiStatusMap = form => state => getOr({}, `form[${form}].aiStatus`, state);
const selectProvenanceMap = form => state => getOr({}, `form[${form}].provenance`, state);

/*
 * Selectors for the AI-review marking (UI design §3.4). Marking is now FORM STATE
 * (aiStatus/provenance in the redux-form slice), not entity state, so accept/reject
 * transitions are local-first and instant. Visibility itself is read live off the
 * redux-form slice's own visibleFields map, so a cascade-hidden field's proposal drops
 * out of every derivation here on its own (§3.2/§3.5).
 */

export const selectFieldAiStatus = (form, name) => state =>
  getOr('none', name, selectAiStatusMap(form)(state));

export const selectFieldProvenance = (form, name) => state =>
  getOr(null, name, selectProvenanceMap(form)(state));

// True once the form carries any SP3-AI marking, proposed or accepted (drives the legend).
export const selectFormHasAiMarkings = form => state =>
  Object.values(selectAiStatusMap(form)(state)).some(status => status && status !== 'none');

export const selectModelAdvisories = createSelector(modelsById, currentModelId, (byId, id) =>
  getOr(null, [id, 'advisories'], byId)
);

export const selectPendingProposalFieldNames = (fieldsByName, modelFormName) =>
  createSelector(
    selectAiStatusMap(modelFormName),
    selectVisibleFieldsMap(modelFormName),
    (aiStatus, visibleFields) =>
      Object.keys(fieldsByName).filter(
        name => getOr('none', name, aiStatus) === 'proposed' && visibleFields[name] !== false
      )
  );

export const selectPendingProposalCountBySection = (fieldsByName, modelFormName) =>
  createSelector(selectPendingProposalFieldNames(fieldsByName, modelFormName), pendingNames =>
    pendingNames.reduce((acc, name) => {
      const section = sectionNameFromFullyQualifiedFieldName(name, fieldsByName);
      return { ...acc, [section]: (acc[section] || 0) + 1 };
    }, {})
  );
